"use client";

import { flexRender } from "@tanstack/react-table";
import { COLUMN_VALUE_FORMAT_OBJ } from "../domain";
import { formatForEdit, parseFromEdit } from "../app";


import { formatCell } from "./cell-renderers";

import type { SmartTableModel } from "./use-smart-table-model";

import type { Cell } from "@tanstack/react-table";

export interface InlineCellParams<TData> {
  m: SmartTableModel<TData>;
  cell: Cell<TData, unknown>;
  columnId: string;
  rowId: string;
  editValue: unknown;
  isInlineEditing: boolean;
  canInlineEdit: boolean;
  colHeader: string;
}

/** Содержимое редактируемой ячейки: отображаемое значение, поле ввода или выбор из списка. */
export function renderInlineCell<TData>({
  m,
  cell,
  columnId,
  rowId,
  editValue,
  isInlineEditing,
  canInlineEdit,
  colHeader,
}: InlineCellParams<TData>) {
  const { lists, t, Select, locale, isBulkSaving, inlineEditCell, setInlineEditCell, setCellEdit, handleInlineEditSave, handleInlineEditCancel } = m;
                            const editList = lists.find(
                              (l) => l.propertyKey === columnId,
                            );
                            const showEditControl =
                              isInlineEditing || (canInlineEdit && !!editList);
                            if (!showEditControl) {
                              return (
                                <span
                                  className="cell-text"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {(() => {
                                    const meta = cell.column.columnDef.meta;
                                    // In bulk mode, show pending edit value instead of original
                                    const displayValue =
                                      editValue !== undefined
                                        ? editValue
                                        : cell.getValue();
                                    // Custom cell render — explicit opt-in via meta.customCell
                                    if (
                                      meta?.customCell &&
                                      cell.column.columnDef.cell
                                    ) {
                                      return flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext(),
                                      );
                                    }
                                    const fmt = meta?.format;
                                    if (fmt) {
                                      const colList = lists.find(
                                        (l) => l.propertyKey === columnId,
                                      );
                                      return formatCell(
                                        displayValue,
                                        fmt,
                                        meta?.formatOptions,
                                        colList?.items,
                                        locale,
                                      );
                                    }
                                    if (cell.column.columnDef.cell) {
                                      return flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext(),
                                      );
                                    }
                                    return String(displayValue ?? "—");
                                  })()}
                                </span>
                              );
                            }
                            const colMeta2 = cell.column.columnDef.meta;
                            const editFmt = colMeta2?.format;
                            const editFmtOpts = colMeta2?.formatOptions;
                            const editPrefix =
                              editFmt === COLUMN_VALUE_FORMAT_OBJ.CURRENCY
                                ? (editFmtOpts?.prefix ?? "$")
                                : editFmtOpts?.prefix;
                            const editSuffix =
                              editFmt === COLUMN_VALUE_FORMAT_OBJ.PERCENT
                                ? "%"
                                : editFmtOpts?.suffix;
                            const editCurrentValue = isInlineEditing
                              ? inlineEditCell!.value
                              : editValue !== undefined
                                ? formatForEdit(editValue, editFmt, editFmtOpts, locale)
                                : formatForEdit(
                                    cell.getValue(),
                                    editFmt,
                                    editFmtOpts,
                                    locale,
                                  );
                            if (editList) {
                              const editOptions = editList.items.map((i) => ({
                                value: i.value,
                                label: i.label[locale] ?? i.value,
                              }));
                              return (
                                <Select
                                  size="sm"
                                  value={editCurrentValue}
                                  onChange={(v) => {
                                    if (isInlineEditing) {
                                      void handleInlineEditSave(v);
                                    } else {
                                      setCellEdit(rowId, columnId, v);
                                    }
                                  }}
                                  ariaLabel={colHeader}
                                  options={editOptions}
                                  disabled={isBulkSaving}
                                />
                              );
                            }
                            const handleEditChange = (raw: string) => {
                              if (isInlineEditing) {
                                setInlineEditCell({
                                  ...inlineEditCell!,
                                  value: raw,
                                });
                              } else {
                                setCellEdit(
                                  rowId,
                                  columnId,
                                  parseFromEdit(raw, editFmt),
                                );
                              }
                            };
                            return (
                              <span className="inline-flex items-center gap-0.5">
                                {editPrefix && (
                                  <span className="text-text-secondary text-xs">
                                    {editPrefix}
                                  </span>
                                )}
                                <input
                                  type="text"
                                  value={editCurrentValue}
                                  onChange={(e) =>
                                    handleEditChange(e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => {
                                    if (isInlineEditing) {
                                      e.target.select();
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (isInlineEditing) {
                                      if (e.key === "Enter") {
                                        e.preventDefault();
                                        void handleInlineEditSave();
                                      } else if (e.key === "Escape") {
                                        e.preventDefault();
                                        handleInlineEditCancel();
                                      }
                                    }
                                  }}
                                  onBlur={() => {
                                    if (isInlineEditing) {
                                      void handleInlineEditSave();
                                    }
                                  }}
                                  aria-label={t(
                                    "table.bulkEdit.aria.editField",
                                    {
                                      column: colHeader,
                                    },
                                  )}
                                  disabled={isBulkSaving}
                                  autoFocus={isInlineEditing}
                                />
                                {editSuffix && (
                                  <span className="text-text-secondary text-xs">
                                    {editSuffix}
                                  </span>
                                )}
                              </span>
                            );
}
